import { askAnswersMatchQuestions, askUserInput, askUserOutput, TRAINER_ACTIVITY_FRAME, TRAINER_PRESENCE_FRAME, TRAINER_TURN_IN_FLIGHT_FRAME, TRAINER_TURN_SETTLED_FRAME, trainerActivityFrame, } from "@repo/contract/agent";
import { WS_CLOSE_AUTH_EXPIRED, WS_CLOSE_UNAUTHENTICATED, } from "@repo/contract/iam";
import { chatSocketUrl } from "./ai-base-url";
import { ANSWER_END_MARKER, assessAnswer, joinAnswerSegments, } from "./answer-integrity";
import { displaySegments, parseAsk, stripAskBlock } from "./ask";
import { ASK_USER_TOOL, CHAT_FRAME, chatRequestFrame, isErrorChunk, OPEN_SCREEN_TOOL, parseFrame, STREAM_RESUME_NONE_IDLE, startMessageId, textOfChunk, toolEventsOfChunk, toolResultFrame, userMessage, } from "./chat-protocol";
import { screenOffer, screenOfferResult, } from "./open-screen";
import { cacheableTail } from "./transcript-cache";
/**
 * The chat transport: one conversation, one socket at a time, and every rule
 * about what to do when that socket goes away.
 *
 * ── WHY THIS IS A PLAIN CLASS AND NOT A HOOK ────────────────────────────────
 * Everything worth getting right here is a state machine over frames and close
 * codes, and none of it needs React. `apps/mobile`'s test suite runs in plain
 * Node with no renderer — deliberately, because a screen rendered in Node
 * proves nothing about the property this app exists to buy (`D04`). Keeping the
 * machine out of the component is what lets the reconnect path, the resume
 * handshake and `T-58`'s retained request id be tested at all. `use-trainer-chat.ts`
 * is the thin subscription on top, and it holds no logic.
 *
 * ── THE THREE WAYS A SOCKET ENDS, AND THEY ARE NOT THE SAME EVENT ───────────
 *
 *  **`4401` — the access token expired (`D36`).** NOT an error and never shown
 *  as one. `D34` authenticates the socket once, at the handshake, and the agent
 *  closes it when the token behind the ticket passes its `exp`. The client's
 *  answer is to refresh, buy a new ticket and reconnect — which is affordable
 *  precisely because `D11` makes the stream resumable, and which `D34`
 *  explicitly chose over letting a conversation outlive its session. Treated as
 *  a failure it would be an error message every fifteen minutes, mid-answer.
 *
 *  **`4403` — no identity envelope.** Should be unreachable: it means something
 *  addressed the agent by a route that is not the ticketed handshake. It is
 *  fatal rather than retried, because retrying would loop.
 *
 *  **Anything else — the network.** A locked screen, a lost signal, a Wi-Fi to
 *  mobile-data handoff. `D11` names those four as the ordinary gym situations
 *  that break naive streaming. Backed off and retried while a turn is in
 *  flight.
 *
 * ── WHAT RECONNECTING ACTUALLY BUYS, MEASURED ───────────────────────────────
 * `apps/ai/src/__tests__/pool/resumable-streaming.test.ts` establishes it in
 * workerd, 7/7: the object keeps generating with nobody attached, chunks land in
 * its SQLite while the answer is still being produced, a keep-alive alarm arms
 * ~30 s out and disarms afterwards, a completed buffer is retained ten minutes,
 * and a reconnecting client gets the WHOLE answer replayed — for ONE billed
 * model call across the disconnect. The replay is full rather than
 * offset-based, which is why {@link TrainerSocket} clears the in-flight text
 * before accepting one. Appending instead would show the first half twice.
 */
/**
 * The close codes, RE-EXPORTED rather than re-declared (`T53`).
 *
 * They were literals here, and literals in `apps/ai` too, with nothing
 * comparing the two. A client whose `4401` drifted from the server's would not
 * error — it would stop recognising "refresh and reconnect" and surface it as a
 * broken chat instead. Both sides now read `@repo/contract`.
 *
 * Re-exported under the names this module already used so the barrel and the
 * screens do not have to move.
 */
export { WS_CLOSE_AUTH_EXPIRED, WS_CLOSE_UNAUTHENTICATED, } from "@repo/contract/iam";
/** Six attempts reaches ~23.5 s of accumulated waiting (0.5 + 1 + 2 + 4 + 8 + 8),
 *  then stops asking and reads the stored transcript instead (`D192`). */
const MAX_RECONNECT_ATTEMPTS = 6;
const BASE_BACKOFF_MS = 500;
const MAX_BACKOFF_MS = 8000;
const DEFAULT_RESUME_TIMEOUT_MS = 15_000;
/**
 * ── THE TURN WATCHDOG, AND WHY THE NUMBER IS THIS NUMBER (`D127`) ───────────
 * How long a turn may be COMPLETELY SILENT before this client declares it
 * dead. It is not a turn timeout, and the difference is the whole design.
 *
 * `D19` argued no timer may ever be armed here, and the argument is right
 * about a SLOW turn and silent about a DEAD one: it assumed the two are
 * indistinguishable. They are not, and the discriminator is not elapsed time —
 * **a healthy turn produces observable progress and a dead one produces
 * nothing at all**. So this measures the GAP between two signs of life on the
 * turn, and every one of them re-arms it: a frame of any kind, a delta, a tool
 * event, and a handshake completing on a fresh socket after a reconnect. A
 * turn that streams for ninety seconds never comes close; a turn behind a
 * server that threw before it published a byte (`D127`'s server half) has no
 * other exit but the person noticing.
 *
 * ── WHY FIVE MINUTES AND NOT THIRTY SECONDS ────────────────────────────────
 * Because `D19` also means the first gap is the WHOLE generation: the server
 * validates a complete answer before publishing any of it, so nothing is on
 * the wire until it is finished. Up to `MAX_TOOL_STEPS = 16` model round trips
 * per attempt, × `DEFAULT_MAX_ATTEMPTS = 2` (the citation validator's
 * regeneration), with a measured single-call outlier of 22.08 s — no window
 * short enough to be useful can tell a slow turn from a dead one by elapsed
 * time alone.
 *
 * ── SO SINCE `D192` IT DECIDES NOTHING, AND IT CAN BE SHORT ────────────────
 * It used to END the turn, which is why it was five minutes: firing on a
 * healthy turn cost a validated answer. Now its expiry only stops listening
 * and reads the stored transcript, where the agent says whether the turn is
 * still running (`ChatTurnInFlight`) — so firing early costs one read, and a
 * socket that died silently (no close event, a frozen app) is noticed in under
 * a minute instead of five. A healthy turn simply goes on in `waiting`, and its
 * answer appears from the transcript.
 *
 * It is a backstop, not the exit. The person's exit is still `abandon()`.
 */
const DEFAULT_TURN_SILENCE_MS = 45_000;
/**
 * ── HOW OFTEN A WAITING TURN READS THE TRANSCRIPT (`D192`) ──────────────────
 * Fast at first, because the common reason to be here is a socket that
 * dropped a moment before an answer that was nearly done; then every ten
 * seconds, which is what a person tolerates between an answer existing and it
 * appearing. Only while the app is in front and online: in the background or
 * offline nothing is read, and coming back reads at once.
 */
const DEFAULT_RESYNC_DELAYS_MS = [
    1000, 2000, 3000, 5000, 8000, 10_000,
];
/**
 * How old a turn may be before an unknown `inFlight` (an agent that predates
 * the field) stops being read as "still writing". The agent's own budget for
 * one turn is under five minutes (`DEFAULT_TURN_SILENCE_MS`).
 */
const UNKNOWN_IN_FLIGHT_BUDGET_MS = 6 * 60_000;
/** The transcript and the in-flight turn, from either shape a read returns. */
function normaliseHistory(read) {
    if (Array.isArray(read)) {
        return { inFlight: undefined, messages: read };
    }
    const shaped = read;
    return { inFlight: shaped.inFlight, messages: shaped.messages };
}
/** A stored pending turn, from either shape it was written in. */
function normalisePending(stored) {
    if (stored === null || stored === undefined) {
        return null;
    }
    return typeof stored === "string" ? { requestId: stored } : stored;
}
/** The device's copy as the first transcript, every message unconfirmed. */
function seedFromCache(cached) {
    return (cached ?? []).map((message) => ({ ...message, cached: true }));
}
function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
/**
 * Unique enough for a key in one conversation, and it needs nothing native.
 *
 * `crypto.randomUUID()` is unavailable in the React Native runtime without a
 * polyfill, and Expo Crypto is a native module that a Node test process
 * cannot load — importing it here would make this whole machine untestable to
 * save nothing, because these ids are not secrets.
 */
function localId() {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
/**
 * One tool event onto the widget list, in place. Reports whether it changed
 * anything — an event this app cannot use, and one it has already applied, are
 * both silent no-ops. Split out of `applyToolEvents` for that method's
 * complexity budget and nothing else; the reasoning is in its header.
 */
function foldToolEvent(asks, event) {
    const index = asks.findIndex((held) => held.toolCallId === event.toolCallId);
    if (event.type === "tool-input-available") {
        // The agent has nine other tools (`D72`) and their calls are server-side;
        // only this one is ever streamed out for a person to answer, so anything
        // else on this wire is not a widget.
        if (event.toolName !== ASK_USER_TOOL || index !== -1) {
            return false;
        }
        const parsed = askUserInput.safeParse(event.input);
        if (!parsed.success) {
            return false;
        }
        asks.push({ input: parsed.data, toolCallId: event.toolCallId });
        return true;
    }
    // `tool-output-available` — the server's copy of an answer. It names no tool,
    // so the only way to know it is a widget's is that a widget with this id is
    // held.
    const widget = index === -1 ? undefined : asks[index];
    if (widget === undefined || widget.output !== undefined) {
        return false;
    }
    const parsed = askUserOutput.safeParse(event.output);
    if (!(parsed.success && askAnswersMatchQuestions(widget.input, parsed.data))) {
        return false;
    }
    asks[index] = { ...widget, output: parsed.data };
    return true;
}
/**
 * The same fold, for `openScreen` (`D112`).
 *
 * A second function rather than a branch inside {@link foldToolEvent}: the two
 * lists hold different types and the `tool-output-available` case is the reason
 * they cannot share one. That event NAMES NO TOOL — the SDK puts `toolName` on
 * the call and not on the output — so the only way to know which control an
 * output belongs to is that one of the two lists is holding its `toolCallId`.
 * Each fold answers for its own list and reports `false` for an id it has never
 * seen, which is what lets both run over every event.
 *
 * Everything not a well-formed offer is dropped in silence, exactly as an
 * unrenderable widget is: a chat with no control still works as a chat, and
 * `screenOffer` refusing an unknown screen is the whole point of it (see
 * `open-screen.ts`).
 */
function foldScreenOfferEvent(offers, event) {
    const index = offers.findIndex((held) => held.toolCallId === event.toolCallId);
    if (event.type === "tool-input-available") {
        if (event.toolName !== OPEN_SCREEN_TOOL || index !== -1) {
            return false;
        }
        const offer = screenOffer(event.input);
        if (offer === null) {
            return false;
        }
        offers.push({ input: offer, toolCallId: event.toolCallId });
        return true;
    }
    const accepted = index === -1 ? undefined : offers[index];
    if (accepted === undefined || accepted.output !== undefined) {
        return false;
    }
    // The server's echo of an acceptance this device sent. Its content is this
    // app's own — nothing else can produce an `openScreen` result — so it is
    // recomputed from the offer rather than parsed back out of the wire, and a
    // second copy of the shape is avoided.
    offers[index] = { ...accepted, output: screenOfferResult(accepted.input) };
    return true;
}
export class TrainerSocket {
    options;
    messages = [];
    status = "idle";
    errorKey = null;
    activity = null;
    /**
     * Whether the stored transcript has been read back yet. See
     * {@link ChatSnapshot.hydrated}; assigned in the constructor for the same
     * literal-type reason `closing` is.
     */
    hydrated;
    socket = null;
    /** True while a close was asked for, so its `onclose` is not a disconnection. */
    closing;
    /**
     * Whether {@link socket} has had its `onopen`, and can therefore be sent on.
     *
     * `socket !== null` is NOT that: the field is assigned the instant
     * `openSocket` returns, seconds before the handshake completes on a bad
     * connection. `clear()` is the only caller that has to know the difference —
     * every other send happens inside a handler that already ran on an open
     * socket. Only meaningful while `socket` is non-null; the close paths null
     * the socket rather than tracking this.
     */
    socketOpen;
    /**
     * A clear that has no socket to go out on yet (`D124`).
     *
     * Set by `clear()` when it had to open a connection, and flushed by
     * `onopen`. Kept as a flag rather than as a queued send because the frame
     * carries nothing: two taps are one clear.
     */
    pendingClear;
    attempts = 0;
    /**
     * ── THE FIELD `T-58` IS ABOUT ───────────────────────────────────────────────
     * The id of the turn currently in flight, retained ACROSS disconnections.
     *
     * While a stream is running the server OFFERS a resume unprompted. Once it has
     * finished, the same reconnect produces `resume_none` with reason `idle` —
     * because a completed stream is not an active one — and nothing offers the
     * answer, even though it is still buffered and still inside its ten-minute
     * retention window. It is reachable only by ACKing the ORIGINAL request id,
     * an id the client was never re-offered and must therefore have remembered.
     *
     * That is the common case, not the rare one: a screen locked for longer than
     * the response takes. Losing this field means losing exactly the answers
     * `D11` exists to protect, and losing them silently — the reconnect succeeds,
     * `resume_none` is not an error, and the partial simply stays on screen.
     */
    inFlightRequestId = null;
    /** The assistant message the in-flight turn is filling in. */
    draftId = null;
    /**
     * The in-flight answer AS IT ARRIVED — end-of-answer marker included.
     *
     * ── WHY THE RAW TEXT IS KEPT SEPARATELY FROM THE MESSAGE ─────────────────
     * `assessAnswer` reads a character the user must never see (`D77`), so
     * the two have irreconcilable requirements: the verdict needs the marker and
     * the screen must not have it. Stripping on the way into the message, and
     * judging this field instead, satisfies both — no component can render or
     * measure a marker that was never written into a `ChatMessage`, and no code
     * path can strip the evidence before it is read.
     *
     * Judging the message's own text instead would be the tempting shortcut and
     * it inverts the guard: a stripped answer is indistinguishable from one that
     * never carried a marker, so every answer would report truncated.
     */
    rawDraft;
    /**
     * The resumed message's own raw text, when this turn is a CONTINUATION that
     * the server writes into an EARLIER message (`D191`) — `""` otherwise.
     *
     * The turn an `askUser` answer wakes is persisted by the SDK in the same
     * assistant message as the turn that asked, and its stream opens with that
     * message's id. Adopted as-is, the continuation's draft took the id of the
     * widget message already on screen: two messages with one id, the text
     * written into the wrong one, and no answer ever visible — observed in a
     * browser on 2026-10-02, the reply arriving only after a reload. So a
     * continuation now MERGES into the message it resumes, exactly as the server
     * stores it: this prefix plus `rawDraft` is the message's whole raw text,
     * while `rawDraft` alone — the new segment — is what `assessAnswer` judges,
     * because the prefix already carries a marker and would vouch for a
     * continuation that was cut off.
     */
    draftPrefix = "";
    /**
     * Whether the turn in flight is a CONTINUATION — the one an `askUser` answer
     * wakes (`D191`). Read when such a turn ends with nothing at all: see
     * {@link refreshMissedContinuation}.
     */
    continuationTurn;
    /** Resume ids already ACKed on this turn, so one offer is answered once. */
    ackedResumeIds = new Set();
    resumeTimer = null;
    /**
     * The turn watchdog (`D127`). Armed with a turn, re-armed by every sign of
     * life on it, cleared by `finishTurn` — the single place a turn ends.
     */
    turnWatchdog = null;
    /**
     * Set when a resume has been ACKed and the replay has not started.
     *
     * ── WHY THE CLEAR HAPPENS ON THE FIRST REPLAYED CHUNK, NOT ON THE ACK ──────
     * The replay is FULL rather than offset-based, so the text already on screen
     * has to go before it arrives or the answer appears twice. The tempting place
     * to do that is the moment the ACK is sent — and it is the wrong one: an ACK
     * that gets no reply (the buffer's ten-minute retention window passed while
     * the phone was away) would then have deleted the partial answer the user
     * had, in exchange for nothing. Deferring the clear to the first chunk means
     * text is only ever discarded when there is text to replace it with.
     */
    awaitingReplay;
    /**
     * A tool answer that has nowhere to go yet.
     *
     * The socket is CLOSED by the time most widgets are tapped: the answer that
     * asked the question finished, and `onResponse` closes the connection on
     * `done` so an idle socket does not keep the Durable Object awake. So the
     * ordinary path for a tap is "buy a ticket, open a socket, then send" — the
     * same deferral `pendingClear` performs, for the same reason, and kept as a
     * separate field because the two can be outstanding at once and a clear must
     * not carry a tool result into a conversation that no longer exists.
     */
    pendingToolResult = null;
    /** {@link pendingToolResult} has gone out on a socket at least once. */
    toolResultSent;
    /**
     * The turn's request has gone out on a socket at least once (`D192`). Until
     * it has, there is nothing on the server to read for — a turn queued
     * offline is SENT when the network returns, not looked for.
     */
    requestSent;
    /** A {@link hydrate} has started and its history read has not settled. */
    hydrating;
    /**
     * A turn asked while {@link hydrating}, whose request has not left.
     *
     * ── WHY THE SEND WAITS FOR THE READ ───────────────────────────────────────
     * `wireMessages` sends the WHOLE transcript on every turn, and the agent
     * upserts every message it receives on its id. Sent while the transcript
     * still holds the device's copy, a conversation cleared on another device
     * would be written back into the Durable Object from this phone's cache —
     * resurrected, with nothing red anywhere. So the question goes on screen at
     * once, as always, and the request leaves when the read has settled: on the
     * server's own transcript when it succeeded, and with every unconfirmed
     * message left out when it failed.
     */
    sendHeld;
    /**
     * A clear landed while the history read was in flight, so whatever the read
     * returns describes a conversation that no longer exists.
     */
    historyVoid;
    /** {@link dispose} ran; a held send must not be released into a new socket. */
    disposed;
    /**
     * The person's question the turn in flight answers (`D192`) — what a read of
     * the stored transcript looks for. `null` for a continuation, which answers
     * a widget instead (see {@link continuationOf}).
     */
    turnQuestionId = null;
    /**
     * For a continuation: the message holding the widget that was answered, and
     * its text before the answer, so a read can tell whether the continuation
     * has landed in it (`D192`).
     */
    continuationOf = null;
    /** When the turn in flight started, for an agent that cannot say. */
    turnStartedAt = 0;
    /** The pending read of the stored transcript (`D192`). */
    resyncTimer = null;
    /** How many reads this waiting turn has made, for the delay schedule. */
    resyncAttempt = 0;
    /** A read is in flight; a second request joins it rather than racing it. */
    resyncing;
    /**
     * The one unprompted resend a turn gets (`D192`), spent when a read finds
     * the question stored, nothing answering it and no answer — an object that
     * died where its recovery could not run. A second such finding offers the
     * button instead of looping.
     */
    autoResent;
    /** The two signal subscriptions, undone by {@link dispose}. */
    unsubscribe = [];
    constructor(options) {
        this.options = options;
        // ── ASSIGNED HERE RATHER THAN AT THE DECLARATION, AND IT IS NOT STYLE ─────
        // `private closing = false` gives the field the literal type `false`, and
        // the linter then reports every `if (this.closing)` in this file as "always
        // falsy" — correctly, by its reading, and wrongly by every other one, since
        // both fields exist precisely to be flipped. Declaring the type and
        // assigning in the constructor is what makes them `boolean`.
        this.closing = false;
        this.awaitingReplay = false;
        this.hydrated = false;
        this.socketOpen = false;
        this.pendingClear = false;
        this.rawDraft = "";
        this.hydrating = false;
        this.sendHeld = false;
        this.historyVoid = false;
        this.disposed = false;
        this.resyncing = false;
        this.autoResent = false;
        this.continuationTurn = false;
        this.toolResultSent = false;
        this.requestSent = false;
        // The device's copy, so the first frame is the conversation rather than
        // nothing. Each message is marked unconfirmed until `hydrate` replaces it.
        this.messages = seedFromCache(options.transcriptCache?.read() ?? null);
        // ── THE TWO MOMENTS A WAITING TURN IS READ AGAIN (`D192`) ──────────────
        // The app coming back to the front, and the network coming back. Both are
        // where an answer the server finished while this screen could not hear it
        // becomes visible — and where a question queued offline leaves.
        if (options.online !== undefined) {
            this.unsubscribe.push(options.online.subscribe((online) => this.onOnlineChange(online)));
        }
        if (options.appActive !== undefined) {
            this.unsubscribe.push(options.appActive.subscribe((active) => this.onActiveChange(active)));
        }
    }
    isOnline() {
        return this.options.online?.current() ?? true;
    }
    isActive() {
        return this.options.appActive?.current() ?? true;
    }
    /**
     * The network came back or went away (`D192`). Back: whatever was queued
     * leaves, and a turn left waiting is read at once. Gone: a turn that is not
     * streaming says so, rather than counting reconnect attempts down to a
     * failure that is only the network.
     */
    onOnlineChange(online) {
        if (this.disposed) {
            return;
        }
        if (!online) {
            if (this.inFlightRequestId !== null && this.socket === null) {
                this.clearResync();
                this.setStatus("offline");
            }
            return;
        }
        this.attempts = 0;
        if (this.pendingClear && this.socket === null) {
            this.connect({ resend: false }).catch(() => undefined);
            return;
        }
        if (this.inFlightRequestId !== null && this.socket === null) {
            this.resumeTurn();
        }
    }
    /**
     * A turn with no socket, picked up again: an outbound half that never left
     * is SENT — there is nothing on the server to look for yet — and anything
     * that did leave is looked for in the transcript.
     */
    resumeTurn() {
        if (this.unsentOutbound()) {
            this.sendAgain();
            return;
        }
        this.resyncNow();
    }
    /** The question, or the widget answer, has never gone out on a socket. */
    unsentOutbound() {
        return this.continuationTurn
            ? this.pendingToolResult !== null && !this.toolResultSent
            : !this.requestSent;
    }
    /**
     * The app came to the front or went to the back (`D192`). The agent is told
     * either way, on the socket if there is one. Back in front, a turn left
     * waiting is read at once, and an idle conversation is read too — another
     * device may have spoken in it.
     */
    onActiveChange(active) {
        if (this.disposed) {
            return;
        }
        this.sendPresence();
        if (!active) {
            this.clearResync();
            return;
        }
        if (this.inFlightRequestId !== null) {
            if (this.socket === null) {
                this.resumeTurn();
            }
            return;
        }
        if (this.hydrated) {
            this.refreshIdle();
        }
    }
    /** Tell the agent whether anybody is looking (`D192`), if there is a socket. */
    sendPresence() {
        // No signal, nothing to say: an agent that hears nothing reads the socket
        // as watched, which is the right default for a caller that cannot tell.
        if (this.options.appActive === undefined ||
            this.socket === null ||
            !this.socketOpen) {
            return;
        }
        this.socket.send(JSON.stringify({ type: TRAINER_PRESENCE_FRAME, visible: this.isActive() }));
    }
    /**
     * Read the transcript with no turn in flight — the app came back — and show
     * what changed: another device's exchange, or a turn of this account's that
     * is still being answered elsewhere, which this screen then waits on.
     */
    refreshIdle() {
        const load = this.options.loadHistory;
        if (load === undefined || this.hydrating) {
            return;
        }
        load()
            .then((loaded) => {
            if (this.disposed || this.inFlightRequestId !== null) {
                return;
            }
            const read = normaliseHistory(loaded);
            this.reconcile(read.messages);
            this.emit();
            this.observeServerTurn(read.inFlight);
        })
            .catch(() => undefined);
    }
    /**
     * A turn the agent is answering that this screen did not start, or whose
     * record it lost (`D192`). Waited on like one of its own — a draft and the
     * typing dots, the composer closed, the transcript read until the answer is
     * in it — because the agent would queue a new question behind it anyway.
     */
    observeServerTurn(inFlight) {
        if (inFlight === null ||
            inFlight === undefined ||
            this.inFlightRequestId !== null ||
            inFlight.continuation) {
            return;
        }
        const newId = this.options.newId ?? localId;
        this.inFlightRequestId = `observed-${newId()}`;
        this.requestSent = true;
        this.turnQuestionId = inFlight.userMessageId;
        this.turnStartedAt = inFlight.startedAt;
        this.appendWaitingDraft();
        this.setStatus("waiting");
        this.scheduleResync();
    }
    /** An empty assistant draft at the end of the transcript, for a wait. */
    appendWaitingDraft() {
        const newId = this.options.newId ?? localId;
        this.draftId = newId();
        this.rawDraft = "";
        this.messages = [
            ...this.messages,
            {
                ask: null,
                id: this.draftId,
                role: "assistant",
                state: "streaming",
                text: "",
            },
        ];
    }
    snapshot() {
        return {
            activity: this.activity,
            errorKey: this.errorKey,
            fromCache: this.messages.some((message) => message.cached === true),
            hydrated: this.hydrated,
            messages: this.messages,
            status: this.status,
        };
    }
    /**
     * Ask a question.
     *
     * The user's message and the empty assistant draft are appended BEFORE
     * anything touches the network, so the question is on screen the instant it is
     * sent. A question that appears only once a socket has opened reads, on a bad
     * connection, as a send button that did nothing.
     */
    ask(text, options = {}) {
        const question = text.trim();
        if (question === "" || this.inFlightRequestId !== null) {
            return;
        }
        const newId = this.options.newId ?? localId;
        this.messages = [
            ...this.messages,
            {
                ask: null,
                ...(options.hidden === true ? { hidden: true } : {}),
                id: newId(),
                role: "user",
                state: "complete",
                text: question,
            },
        ];
        this.startTurn();
    }
    /**
     * Ask the last discarded question again — the refresh button on the user's
     * own message (`D77`).
     *
     * ── WHY THE QUESTION IS REUSED RATHER THAN RE-TYPED ──────────────────────
     * Because it is still on screen, and it is still the last thing in the
     * transcript: the answer to it was removed, not the question. Appending a
     * copy would show the user their own words twice for a turn they never asked
     * twice, and would send the model a repeated question as if it were one.
     *
     * Everything after that question is dropped first. After a discard there is
     * nothing there; after `abandon()` there is the partial the user chose to
     * stop, and that partial must not survive into the turn that replaces it —
     * `wireMessages` already refuses to send it to the model, and leaving it on
     * screen above the new answer would read as two answers to one question.
     */
    resend() {
        if (this.inFlightRequestId !== null) {
            return;
        }
        const question = this.messages
            .filter((message) => message.role === "user" && message.resendable)
            .at(-1);
        if (question === undefined) {
            return;
        }
        const index = this.messages.indexOf(question);
        this.messages = [
            ...this.messages.slice(0, index),
            { ...question, resendable: false },
        ];
        this.startTurn();
    }
    /**
     * Answer one `askUser` widget (`D122`).
     *
     * ── WHAT THIS REFUSES, AND WHY IT NEVER THROWS ──────────────────────────────
     * Four ways in, and all four return silently, the way `parseAsk` returns
     * `null` for a malformed envelope: no widget with that id, a widget already
     * answered, an output the contract rejects, and an output whose answer count
     * does not match the question count (`askAnswersMatchQuestions` — the one
     * rule neither schema can carry, because it is a relation between two of
     * them). A tap that cannot be honoured leaves the widget armed; a throw from
     * a press handler would take the screen down over a control the person can
     * simply tap again.
     *
     * "Already answered" is the idempotence that matters most: a double tap sends
     * ONE frame. The server's own rule is that the first terminal result wins, so
     * a second frame would be silently discarded there — but it would also open a
     * second continuation here, and two drafts for one answer is a transcript
     * with a permanently empty bubble in it.
     *
     * ── THE ANSWER IS MARKED BEFORE IT IS SENT ────────────────────────────────
     * The widget shows what was chosen the instant it is chosen, because the
     * continuation is a full model turn and a control that stays armed for
     * several seconds gets tapped again. The mark is local truth about a local
     * act; the server's copy arrives later as the tool part's `output`, and
     * `applyToolEvents` treats that as a no-op on an already-answered widget.
     */
    answerAskWidget(toolCallId, output) {
        // A turn in flight owns the draft and the socket. The ordinary case is not
        // this: the answer that asked the question is finished by the time a widget
        // is tappable.
        if (this.inFlightRequestId !== null) {
            return;
        }
        // A widget on an unconfirmed message is refused: it may have been answered
        // on another device since this copy was written (`ChatMessage.cached`).
        const widget = this.messages
            .filter((message) => message.cached !== true)
            .flatMap((message) => message.asks ?? [])
            .find((candidate) => candidate.toolCallId === toolCallId);
        if (widget === undefined || widget.output !== undefined) {
            return;
        }
        const parsed = askUserOutput.safeParse(output);
        if (!(parsed.success && askAnswersMatchQuestions(widget.input, parsed.data))) {
            return;
        }
        this.attachOutput(toolCallId, parsed.data);
        this.dispatchToolResult(toolResultFrame({ output: parsed.data, toolCallId }), toolCallId);
    }
    /**
     * Accept one `openScreen` offer (`D112`) — the person tapped it, the screen
     * is being navigated to, and the model is told so.
     *
     * The same four refusals as {@link answerAskWidget} minus the two that are
     * about answers: a turn in flight, no offer with that id, and an offer
     * already accepted. All silent, for that method's reason — a throw out of a
     * press handler takes the screen down over a control that could simply be
     * tapped again.
     *
     * ── THE OUTPUT IS BUILT HERE, NOT TAKEN FROM THE CALLER ───────────────────
     * The screen passes an id and nothing else. `answerAskWidget` has to accept
     * an `output` because the person AUTHORS it — which chips they chose — and
     * the contract's schema is what makes that safe. An acceptance authors
     * nothing: it is entirely determined by the offer this class is already
     * holding, so there is no shape to validate and no way for a caller to send
     * one that disagrees with the control that was drawn.
     *
     * ── MARKED BEFORE IT IS SENT, AND BEFORE THE NAVIGATION ───────────────────
     * The tap leaves this screen immediately, and the continuation it starts runs
     * while the person is elsewhere. Marking first is what makes coming back show
     * a record of the screen that was opened rather than a live control offering
     * to open it again.
     *
     * ── IT REPORTS, WHERE `answerAskWidget` DOES NOT, AND THAT IS THE POINT ───
     * A refused answer leaves a widget armed and costs nothing else. A refused
     * ACCEPTANCE has a second half — the navigation — and the two must not come
     * apart: a screen opened without the model being told leaves the tool call
     * pending for ever (the person is on the editor, the conversation is still
     * waiting), and a model told about a screen that never opened is worse. So
     * the caller navigates on `true` and does nothing on `false`, and the offer
     * stays armed to be tapped again when the turn is over.
     */
    acceptScreenOffer(toolCallId) {
        // A turn in flight owns the socket, and a continuation opened under one is
        // a second draft for one answer. Rare — an offer is drawn by the answer
        // that ends the turn — and recoverable, because the control stays armed.
        if (this.inFlightRequestId !== null) {
            return false;
        }
        const offer = this.messages
            .filter((message) => message.cached !== true)
            .flatMap((message) => message.offers ?? [])
            .find((candidate) => candidate.toolCallId === toolCallId);
        if (offer === undefined || offer.output !== undefined) {
            return false;
        }
        const output = screenOfferResult(offer.input);
        this.attachOfferOutput(toolCallId, output);
        this.dispatchToolResult(toolResultFrame({ output, toolCallId, toolName: OPEN_SCREEN_TOOL }), toolCallId);
        return true;
    }
    /**
     * Put one tool result on the wire, or hold it until there is a wire.
     *
     * Shared by both client tools because the connection states it has to cover
     * are the transport's and not the control's: a widget answered and an offer
     * accepted differ in what they carry and in nothing else.
     */
    dispatchToolResult(frame, toolCallId) {
        // ── THE TURN OPENS AT THE TAP, NOT WHEN THE FRAME LEAVES (`D192`) ──────
        // It used to open on the send, so a tap with no socket — offline, or a
        // ticket that failed — left no turn in flight: the composer reopened, the
        // frame sat held in memory, and the next QUESTION's connection flushed the
        // held answer first and returned, so the question was never sent and its
        // bubble spun for ever. Opened here, the turn holds the composer shut
        // until its answer arrives, and the frame is remembered outside the
        // process so a kill does not lose the person's choice either.
        this.openContinuation(toolCallId);
        this.pendingToolResult = frame;
        this.toolResultSent = false;
        this.options.pendingTurn
            ?.remember(this.inFlightRequestId, { toolResult: frame })
            .catch(() => undefined);
        const { socket } = this;
        if (socket !== null && this.socketOpen) {
            this.flushToolResult(socket);
            return;
        }
        if (socket === null) {
            this.connect({ resend: false }).catch(() => undefined);
        }
        // Otherwise a connection is already on its way and its `onopen` flushes the
        // frame — opening a second socket would spend a second ticket and orphan
        // the first.
    }
    /**
     * Send a deferred tool answer on a socket that has opened. Reports whether it
     * did, because the caller must then do nothing else with this connection.
     */
    flushToolResult(socket) {
        const frame = this.pendingToolResult;
        if (frame === null || this.toolResultSent) {
            return false;
        }
        // Kept after the send, not dropped: if this socket dies before the agent
        // applied it, a read that finds the widget still unanswered on the server
        // sends it again (`D192`). The agent's "first terminal result wins" makes
        // a second copy harmless.
        this.toolResultSent = true;
        socket.send(JSON.stringify(frame));
        return true;
    }
    /**
     * A draft for the answer the tool result unblocks.
     *
     * The SDK continues the turn on `autoContinue`, and the continuation streams
     * back through the ordinary `cf_agent_use_chat_response` path — which writes
     * into `draftId` and nowhere else, so without a draft every chunk of it is
     * dropped by `updateDraft` and the person watches a spinner over nothing.
     *
     * ── THE REQUEST ID HERE IS LOCAL AND NAMES NO BUFFER ──────────────────────
     * The continuation's real request id is minted by the SDK inside the object,
     * and is never told to this client. So this id cannot be ACKed and is
     * deliberately NOT written to `pendingTurn`: remembering it would have the
     * next mount reconnect and ACK an id the server has never heard of, then
     * report the answer lost when nothing replays. What it IS is the "a turn is
     * in flight" flag every guard in this class reads — `ask`, `resend` and
     * `answerAskWidget` must all refuse while the continuation runs, and
     * `abandon()` must be able to end it.
     */
    openContinuation(toolCallId) {
        this.continuationTurn = true;
        const holder = this.messages.find((message) => message.asks?.some((widget) => widget.toolCallId === toolCallId) ===
            true ||
            message.offers?.some((offer) => offer.toolCallId === toolCallId) ===
                true);
        this.continuationOf =
            holder === undefined
                ? null
                : {
                    messageId: holder.id,
                    text: holder.wire ?? holder.text,
                    toolCallId,
                };
        this.turnQuestionId = null;
        this.turnStartedAt = Date.now();
        this.autoResent = false;
        const newId = this.options.newId ?? localId;
        this.draftId = newId();
        this.inFlightRequestId = newId();
        this.rawDraft = "";
        this.errorKey = null;
        this.ackedResumeIds.clear();
        this.messages = [
            ...this.messages,
            {
                ask: null,
                id: this.draftId,
                role: "assistant",
                state: "streaming",
                text: "",
            },
        ];
        // Assigned rather than routed through `setStatus`, which skips the emit
        // when the status has not changed — and the new draft still has to reach
        // the screen.
        this.status = "streaming";
        // A continuation is a turn and gets the same watchdog (`D127`). It is also
        // the turn with the least other cover: its request id is local and names
        // no buffer, so no resume can ever end it.
        this.armTurnWatchdog();
        this.emit();
    }
    /**
     * Open a turn for the question already at the end of the transcript.
     *
     * Shared by `ask` and `resend` because everything except where the question
     * came from is identical — and because the two ids and the four reset fields
     * are exactly the kind of list that goes out of step when it is written
     * twice. The empty assistant draft is appended BEFORE anything touches the
     * network, so the turn is on screen the instant it is asked; a question that
     * appears only once a socket has opened reads, on a bad connection, as a send
     * button that did nothing.
     */
    startTurn() {
        const newId = this.options.newId ?? localId;
        this.draftId = newId();
        this.inFlightRequestId = newId();
        const question = this.messages
            .filter((message) => message.role === "user")
            .at(-1);
        this.turnQuestionId = question?.id ?? null;
        this.continuationOf = null;
        this.turnStartedAt = Date.now();
        this.autoResent = false;
        this.requestSent = false;
        // Outside the process before the request leaves, not after: the answer this
        // id unlocks can finish while the app is gone, and an id written on
        // completion would be written by the code that is no longer running.
        // The QUESTION goes with it (`D192`): a turn queued offline, or one whose
        // request never left before the app was killed, is sent on the next mount
        // instead of being a bubble that was never asked.
        this.options.pendingTurn
            ?.remember(this.inFlightRequestId, question === undefined
            ? undefined
            : {
                question: {
                    ...(question.hidden === true ? { hidden: true } : {}),
                    id: question.id,
                    text: question.text,
                },
            })
            .catch(() => undefined);
        this.ackedResumeIds.clear();
        this.rawDraft = "";
        this.errorKey = null;
        this.messages = [
            // Every stale offer goes: a user who typed a new question instead of
            // tapping refresh has answered the offer, and leaving the flag set would
            // let a later tap re-ask a question from three turns ago.
            ...this.messages.map((message) => message.resendable === true
                ? { ...message, resendable: false }
                : message),
            {
                ask: null,
                id: this.draftId,
                role: "assistant",
                state: "streaming",
                text: "",
            },
        ];
        this.setStatus("connecting");
        // Armed with the turn rather than with the socket, so a `buyTicket` that
        // never settles is covered too (`D127`). `onopen` re-arms it, which is
        // what stops the handshake from spending the model's budget.
        this.armTurnWatchdog();
        if (this.hydrating) {
            // On screen now, on the wire once the read settles. See `sendHeld`.
            this.sendHeld = true;
            return;
        }
        // Deliberately not awaited: this runs from a press handler and must not
        // make the caller deal with a promise. Every failure inside lands on
        // `fail`, which is a state change rather than a rejection.
        this.connect({ resend: true }).catch(() => undefined);
    }
    /**
     * Rebuild the screen from the server, and reclaim an answer that finished
     * while nobody was watching (`T-58`).
     *
     * Called once on mount. Two halves, and the second only runs because of the
     * first: the transcript comes back whole from the Durable Object's own
     * storage, and then — if this device left a turn in flight — the retained
     * request id is restored and a socket is opened solely to ACK it. That ACK is
     * the only way a COMPLETED stream is ever replayed; the server never offers
     * one, because a finished stream is not an active one.
     *
     * Nothing here throws. A history read that fails leaves the screen as it
     * was — the device's copy, still unconfirmed, or empty without one — because
     * an error banner over a conversation says less than the conversation does.
     *
     * ── THE SERVER WINS (the device copy, `transcript-cache.ts`) ──────────────
     * A read that succeeds replaces every cached message with the server's list;
     * an EMPTY list is a conversation cleared or deleted elsewhere, so the copy
     * goes with it. What was added here since opening — a question typed while
     * the read was in flight — is kept, after the server's list, and its request
     * leaves only now (`sendHeld`).
     */
    async hydrate() {
        this.hydrating = true;
        const loaded = await this.options.loadHistory?.().catch(() => null);
        this.hydrating = false;
        const read = loaded === null || loaded === undefined ? null : normaliseHistory(loaded);
        // `undefined` is no history port at all, and like a failed read it
        // confirms nothing — the copy, if any, stays as it is.
        if (read !== null && !this.historyVoid) {
            this.reconcile(read.messages);
        }
        this.historyVoid = false;
        // HERE, and not at the end of this method: everything below is the pending
        // turn — a socket, a handshake and possibly a replay, seconds of work that
        // the screen's opening decision must not wait on. The history read is
        // settled by this line whichever way it went, which is the whole question
        // the flag answers. The emit is unconditional for the same reason: the
        // screen has to re-render on a read that found nothing, because "nothing,
        // for certain" is exactly the news.
        this.hydrated = true;
        this.emit();
        if (this.sendHeld) {
            this.sendHeld = false;
            if (!this.disposed && this.inFlightRequestId !== null) {
                // The held question owns this mount's connection, exactly as a
                // question asked after the read would; the pending-turn check below
                // then returns at its own `inFlightRequestId` guard.
                this.connect({ resend: true }).catch(() => undefined);
            }
        }
        const pending = normalisePending(await this.options.pendingTurn?.read().catch(() => null));
        if (this.closing || this.disposed || this.inFlightRequestId !== null) {
            return;
        }
        if (pending === null) {
            // Nothing of this device's in flight — but the agent may be answering a
            // turn of this account's anyway, from another device or from a record
            // this one lost. Waited on like our own (`observeServerTurn`).
            this.observeServerTurn(read?.inFlight);
            return;
        }
        this.restorePendingTurn(pending);
    }
    /**
     * ── A TURN LEFT IN FLIGHT BY A PREVIOUS MOUNT (`T-58`, `D192`) ────────────
     * The app was closed, killed or sent away while a turn was running. What
     * happens next is decided by the stored transcript, never by a timer and
     * never by ACKing an id blind:
     *
     *  - the answer is stored → the turn is over, and the transcript the read
     *    just drew already shows it. It used to be resumed ANYWAY: the replay
     *    reused the stored answer's id, was merged into it, and the answer
     *    appeared twice — and the doubled text went back over the stored row on
     *    the next turn (`T-203`).
     *  - the agent is still answering → wait, reading the transcript.
     *  - the question never reached the agent → it is in the record: send it.
     *  - received and not answered → one resend, then the button.
     *
     * A widget answer restores the same way, from its frame.
     */
    restorePendingTurn(pending) {
        this.inFlightRequestId = pending.requestId;
        // Whether it left is not known — that is what the read is for — so it is
        // treated as sent, and a read that finds it missing sends it.
        this.requestSent = true;
        this.turnStartedAt = Date.now();
        this.autoResent = false;
        if (pending.toolResult === undefined) {
            const { question } = pending;
            if (question !== undefined &&
                !this.messages.some((message) => message.id === question.id)) {
                // The question never made it into a transcript the server confirmed:
                // put it back where the person left it, then let the read decide.
                this.messages = [
                    ...this.messages,
                    {
                        ask: null,
                        ...(question.hidden === true ? { hidden: true } : {}),
                        id: question.id,
                        role: "user",
                        state: "complete",
                        text: question.text,
                    },
                ];
            }
            this.turnQuestionId =
                question?.id ??
                    this.messages.filter((message) => message.role === "user").at(-1)?.id ??
                    null;
            if (this.turnQuestionId === null) {
                // A record with no question over a transcript with none: there is
                // nothing to send and nothing to wait for.
                this.finishTurn();
                return;
            }
            this.continuationOf = null;
            this.appendWaitingDraft();
        }
        else {
            const { toolCallId } = pending.toolResult;
            this.pendingToolResult = pending.toolResult;
            this.toolResultSent = false;
            const id = typeof toolCallId === "string" ? toolCallId : "";
            // `openContinuation` re-mints the request id; the stored one is kept,
            // because it is what `pendingTurn` holds.
            this.openContinuation(id);
            this.inFlightRequestId = pending.requestId;
        }
        this.setStatus(this.isOnline() ? "waiting" : "offline");
        this.emit();
        this.resyncNow();
    }
    /**
     * The server's transcript over the device's copy. See "THE SERVER WINS" on
     * {@link hydrate}.
     */
    reconcile(restored) {
        const serverIds = new Set(restored.map((message) => message.id));
        const local = this.messages.filter((message) => message.cached !== true && !serverIds.has(message.id));
        this.messages = [...restored, ...local];
        const tail = cacheableTail(restored);
        if (tail.length === 0) {
            this.options.transcriptCache?.forget();
        }
        else {
            this.options.transcriptCache?.write(tail);
        }
    }
    /**
     * The settled tail back into the device's copy — only from a transcript the
     * server has confirmed. While any message is still unconfirmed (the read
     * failed) the copy is left as it is: writing the mix would make tomorrow's
     * first frame from messages nothing ever confirmed.
     */
    persistTranscript() {
        if (this.messages.some((message) => message.cached === true)) {
            return;
        }
        const tail = cacheableTail(this.messages);
        if (tail.length > 0) {
            this.options.transcriptCache?.write(tail);
        }
    }
    /** Tear everything down. Called when the screen unmounts. */
    dispose() {
        this.disposed = true;
        // A held send is NOT forgotten any more (`D192`): its question is in
        // `pendingTurn` with its id, and the next mount sends it — a question
        // typed into a screen closed before the history read finished is still
        // a question the person asked.
        this.sendHeld = false;
        this.closing = true;
        this.clearResumeTimer();
        this.clearTurnWatchdog();
        this.clearResync();
        for (const unsubscribe of this.unsubscribe.splice(0)) {
            unsubscribe();
        }
        this.socket?.close();
        this.socket = null;
    }
    /**
     * Give up on the answer that is stuck, and let the user ask again.
     *
     * Keeps the partial text on screen, marked `truncated`, rather than deleting
     * it — the one place `D77`'s discard does NOT apply, because the user pressed
     * the button and therefore already knows the answer is unfinished. Half an
     * answer they asked to see is more useful than a blank bubble, and deleting
     * it would hide the fact that something arrived. The resend button is offered
     * on the question as well, so the tap is the same one either way.
     */
    abandon() {
        if (this.inFlightRequestId === null) {
            return;
        }
        this.settleDraft("truncated");
        this.offerResend();
        // ── AND THE TURN IS RELEASED, WHICH IT WAS NOT BEFORE ────────────────────
        // `abandon` cleared no state, so `inFlightRequestId` stayed set and the
        // guard at the top of `startTurn`'s callers refused every question after
        // it: giving up on one answer ended the conversation, silently, for as long
        // as the screen stayed open. Invisible until now because nothing could ask
        // again from this state; `resend()` can, and is guarded on the same field.
        this.finishTurn();
        this.closing = true;
        this.socket?.close();
        this.socket = null;
        this.setStatus("idle");
    }
    /**
     * Start the conversation again from zero (`D124`).
     *
     * ── WHAT IS DESTROYED, AND WHAT CANNOT BE ─────────────────────────────────
     * The words, and only the words. Every fact the trainer knows — profile,
     * training config, equipment, injuries, PAR-Q, plan, measurements — is a row
     * in D1 behind the API that the agent reads over RPC on each turn, so an
     * onboarding cleared halfway resumes from STORED progress and only the
     * answers that were still a draft on this phone are asked again. Nothing in
     * this method, or on the server's side of this frame, touches a stored row.
     *
     * ── THE SERVER HALF IS THE SDK's, AND IT IS COMPLETE ──────────────────────
     * The frame deletes the message table, wipes the resumable-stream buffers —
     * so a turn that was pending before the clear cannot replay into the fresh
     * conversation — aborts anything in flight, and broadcasts the clear to
     * every other connection. No route was written for this and none is needed;
     * `admit()` also runs before the frame is looked at, so a frozen
     * conversation (`D79`) already refuses.
     *
     * ── AND THE LOCAL RESET RELEASES THE TURN ─────────────────────────────────
     * `inFlightRequestId` in particular: the server aborts the turn, so leaving
     * the id set would leave the guard at the top of `ask()` refusing every
     * question after a clear — a fresh, empty conversation that cannot be
     * spoken to. `hydrated` is deliberately NOT reset: an empty transcript after
     * a clear is the truth, not a read that has not landed, and clearing the
     * flag would re-arm the opening effect's guard against a state that is
     * already settled.
     */
    clear() {
        this.pendingClear = true;
        // BEFORE the send, so `flushClear` sees a released turn and can close the
        // socket behind the frame.
        this.resetToEmpty();
        const { socket } = this;
        if (socket === null) {
            // The same path a turn takes: buy a ticket, open a socket. The frame
            // goes out from `onopen`. The status stays `idle` throughout — there is
            // no turn to wait for, and a spinner over an empty transcript would say
            // an answer is coming.
            this.connect({ resend: false }).catch(() => undefined);
            return;
        }
        if (this.socketOpen) {
            this.flushClear(socket);
        }
        // Otherwise a connection is already on its way and its `onopen` will flush
        // the flag — opening a second socket for it would spend a second ticket
        // and orphan the first.
    }
    /**
     * Send the clear frame on a socket that has opened, once.
     *
     * The socket is closed behind it for the reason the finished-answer path
     * closes one: the conversation lives in the Durable Object, not in this
     * connection, and an idle socket keeps that object awake for a conversation
     * nobody is having. Skipped if a turn is somehow in flight — someone asked a
     * question between the clear and this handshake — because that turn owns the
     * connection.
     */
    flushClear(socket) {
        if (!this.pendingClear) {
            return;
        }
        this.pendingClear = false;
        socket.send(JSON.stringify({ type: CHAT_FRAME.CHAT_CLEAR }));
        if (this.inFlightRequestId !== null || this.socket !== socket) {
            return;
        }
        this.closing = true;
        socket.close();
        this.socket = null;
        this.socketOpen = false;
    }
    /**
     * Everything local goes: the transcript, the turn, the error.
     *
     * Shared by the outbound clear and by an inbound one — another device
     * cleared, and the server broadcast it — because the two must leave this
     * screen in exactly the same state, and a second copy of this list is a
     * second place for it to go out of step.
     */
    resetToEmpty() {
        // The turn is over by decree: the server aborted it, so the stored request
        // id names a buffer nobody will ACK.
        this.finishTurn();
        this.messages = [];
        // The device's copy goes too, and a history read still in flight returns
        // the conversation as it was before this clear.
        this.options.transcriptCache?.forget();
        if (this.hydrating) {
            this.historyVoid = true;
        }
        // The widget it answers is gone with the transcript, and the server has
        // aborted the turn that opened the call, so the frame would name a tool
        // call nothing is waiting on.
        this.pendingToolResult = null;
        this.errorKey = null;
        // Assigned rather than routed through `setStatus`, which would skip the
        // emit when the status is already `idle` — and the transcript still has to
        // reach the screen.
        this.status = "idle";
        this.emit();
    }
    // ── Connection ───────────────────────────────────────────────────────────
    async connect(options) {
        this.closing = false;
        this.socketOpen = false;
        if (!this.isOnline()) {
            // ── NO NETWORK IS A QUEUE, NOT A FAILURE (`D192`) ────────────────────
            // A ticket purchase can only fail here, and six backed-off failures used
            // to end in "we could not get back to your answer" over a question that
            // had never left the phone. The turn waits instead; the network coming
            // back is what sends it (`onOnlineChange`).
            if (this.inFlightRequestId !== null) {
                this.clearTurnWatchdog();
                this.setStatus("offline");
            }
            return;
        }
        let url;
        try {
            // ONE ticket, bought now, used once. Buying it before the socket rather
            // than caching it is the whole of `D34`'s 60-second argument.
            const [{ ticket }, base] = await Promise.all([
                this.options.buyTicket(),
                this.options.aiBaseUrl(),
            ]);
            url = chatSocketUrl({
                base,
                conversationId: this.options.conversationId,
                ...(this.options.subjectAccountId === undefined
                    ? {}
                    : { subjectAccountId: this.options.subjectAccountId }),
                ticket,
            });
        }
        catch (error) {
            // A refused ticket is a session problem, and it is the ONE failure that
            // must not be retried: `ApiClient` has already decided the session is
            // over, and asking again would spend refresh tokens against a family the
            // server has revoked.
            this.retryOrFail(error);
            return;
        }
        if (url === null) {
            this.fail("chat.misconfigured");
            return;
        }
        const socket = this.options.openSocket(url);
        this.socket = socket;
        socket.onopen = () => {
            this.attempts = 0;
            this.socketOpen = true;
            // First on every connection: whether anybody is looking, which is how
            // the agent decides that a finished answer deserves a push (`D192`).
            this.sendPresence();
            if (this.inFlightRequestId !== null) {
                // A completed handshake is a sign of life on the turn, and it is the
                // one that keeps the watchdog off a legitimate reconnect (`D36` closes
                // this socket every fifteen minutes by design): the window restarts
                // from the moment there is a connection to receive on, so the seconds
                // spent buying a ticket and reconnecting are never charged to the
                // model's budget.
                this.armTurnWatchdog();
            }
            // Before anything else this connection might do: a clear that had to
            // wait for a socket is the reason this one exists, and the server aborts
            // whatever it finds anyway.
            this.flushClear(socket);
            // A tool answer waiting for a socket OWNS this connection: the
            // continuation it opens is a turn, and everything below would then ask
            // for a second one — a `use_chat_request` for a question already
            // answered, or a resume probe for an id the server never minted.
            if (this.flushToolResult(socket)) {
                return;
            }
            this.setStatus(this.inFlightRequestId === null ? "idle" : "streaming");
            if (options.resend && this.inFlightRequestId !== null) {
                // No timer is armed here, deliberately. `D19` makes the server generate
                // the WHOLE answer and validate its citations before a byte of it is
                // published — and regenerate once if the validator rejects it — so time
                // to first token is a full model call and can legitimately be long. A
                // timeout short enough to be useful would fire on a healthy slow turn.
                // The way out of a turn that never answers is `abandon()`, which the
                // screen offers as a button rather than guessing on the user's behalf.
                socket.send(JSON.stringify(chatRequestFrame({
                    messages: this.wireMessages(),
                    requestId: this.inFlightRequestId,
                })));
                this.requestSent = true;
                return;
            }
            if (this.inFlightRequestId !== null) {
                // A reconnection into an unfinished turn. Ask what there is to resume;
                // the server may also have offered already, inside `onConnect`.
                socket.send(JSON.stringify({
                    probeId: (this.options.newId ?? localId)(),
                    type: CHAT_FRAME.STREAM_RESUME_REQUEST,
                }));
                this.armResumeTimer();
            }
        };
        socket.onmessage = (event) => {
            // The same identity guard `onclose` carries below, and for a stronger
            // reason. A settled turn detaches the socket (`this.socket = null`) but
            // does NOT detach this handler, so a frame arriving late on a superseded
            // — or already closed — connection was still dispatched into a machine
            // that had moved on. `onResponse` then put the status back to
            // `streaming` with no draft to write into and no turn to end it: the
            // screen kept `chat.thinking` over a finished answer, no timer was armed
            // to break it, this socket's `onclose` was guarded out, and `abandon()`
            // returned at its own null check, so the button offered as the way out
            // did nothing.
            if (this.socket !== socket) {
                return;
            }
            const frame = parseFrame(event.data);
            if (frame !== null) {
                this.onFrame(frame);
            }
        };
        // `onerror` is not a close. React Native fires both, in that order, and
        // acting on the first would start a reconnection while the old socket is
        // still open — two sockets, two tickets, and a replay landing in a draft
        // the other one is also writing to.
        socket.onerror = () => undefined;
        socket.onclose = (closeEvent) => {
            if (this.socket !== socket) {
                return;
            }
            this.socket = null;
            this.onClose(closeEvent.code ?? 1006);
        };
    }
    onClose(code) {
        this.clearResumeTimer();
        if (this.closing) {
            return;
        }
        if (code === WS_CLOSE_UNAUTHENTICATED) {
            // Unreachable through the ticketed handshake. Retrying would loop against
            // a route that has already decided this connection has no identity.
            this.fail("chat.unavailable");
            return;
        }
        if (this.inFlightRequestId === null) {
            // Nothing to lose. The next question opens a new socket.
            this.setStatus("idle");
            return;
        }
        if (code === WS_CLOSE_AUTH_EXPIRED) {
            // `D36`, working exactly as designed. The attempt counter is NOT
            // incremented: this is a scheduled event on a fifteen-minute token, not a
            // failing network, and counting it would exhaust the budget on a long
            // conversation that never once had a problem.
            this.setStatus("reconnecting");
            this.connect({ resend: false }).catch(() => undefined);
            return;
        }
        this.setStatus("reconnecting");
        this.reconnectWithBackoff().catch(() => undefined);
    }
    async reconnectWithBackoff() {
        if (this.attempts >= MAX_RECONNECT_ATTEMPTS) {
            // ── OUT OF ATTEMPTS IS NOT OUT OF ANSWER (`D192`) ─────────────────────
            // This used to discard the draft and say "we could not get back to your
            // answer" — while the agent, which keeps generating with nobody
            // attached, finished and stored it. The socket is what failed, not the
            // turn: the transcript is read instead, until the answer is in it.
            this.enterWaiting();
            return;
        }
        const wait = Math.min(BASE_BACKOFF_MS * 2 ** this.attempts, MAX_BACKOFF_MS);
        this.attempts += 1;
        await (this.options.delay ?? sleep)(wait);
        if (this.closing || this.inFlightRequestId === null) {
            return;
        }
        await this.connect({ resend: false });
    }
    retryOrFail(error) {
        // `SessionExpiredError` is matched by NAME rather than by `instanceof`.
        // Importing the class would pull `src/lib/api`'s barrel — and with it
        // Expo SecureStore, a native module — into a machine that is otherwise
        // pure and testable in Node. The name is part of that class's public
        // surface and is asserted in `__tests__/trainer-socket.test.ts`.
        const thrown = error;
        if (thrown?.name === "SessionExpiredError") {
            this.settleDraft("failed");
            this.fail("auth.session_expired");
            return;
        }
        // A 401 that survived `authorizedFetch` is not a stale access token — that
        // path already refreshed and retried once. It is the server refusing a
        // credential it has just been handed a fresh one for, and asking six more
        // times over sixteen seconds would neither succeed nor tell the user
        // anything the first refusal did not.
        if (thrown?.name === "ApiError" && thrown.status === 401) {
            this.settleDraft("failed");
            this.fail("auth.session_expired");
            return;
        }
        if (this.inFlightRequestId === null) {
            this.setStatus("idle");
            return;
        }
        this.setStatus("reconnecting");
        this.reconnectWithBackoff().catch(() => undefined);
    }
    // ── Frames ───────────────────────────────────────────────────────────────
    onFrame(frame) {
        if (this.inFlightRequestId !== null) {
            // ── PROGRESS ON THE WIRE, WHICH IS THE DISCRIMINATOR (`D127`) ─────────
            // Re-armed BEFORE the frame is dispatched, and by every frame rather
            // than by a delta: `cf_agent_stream_pending`, a resume offer and the
            // server's copy of the transcript are all evidence that something is
            // alive at the other end, and a turn that is alive must never be killed
            // for being slow. Dispatch may end the turn a line later — `finishTurn`
            // clears this timer on every route out, so re-arming first cannot leave
            // one behind.
            this.armTurnWatchdog();
        }
        switch (frame.type) {
            case CHAT_FRAME.CHAT_CLEAR:
                // Another device cleared and the server broadcast it (`D124`). The
                // same local reset, because the conversation this screen is showing no
                // longer exists on the server — leaving the transcript up would be a
                // screen full of messages that the next hydrate deletes.
                this.resetToEmpty();
                break;
            case CHAT_FRAME.STREAM_RESUMING:
                this.onResumeOffer(frame);
                break;
            case CHAT_FRAME.STREAM_RESUME_NONE:
                this.onResumeNone(frame);
                break;
            case CHAT_FRAME.USE_CHAT_RESPONSE:
                this.onResponse(frame);
                break;
            case TRAINER_ACTIVITY_FRAME:
                this.onActivity(frame);
                break;
            case TRAINER_TURN_SETTLED_FRAME:
            case TRAINER_TURN_IN_FLIGHT_FRAME:
                this.onRepeatedTurn(frame);
                break;
            default:
                // `cf_agent_chat_messages`, `cf_agent_stream_pending` and
                // `cf_agent_chat_recovering` all land here. The first is the server's
                // copy of the transcript and is ignored — this client keeps its own,
                // and in any case the SDK broadcasts that frame with the ORIGINATING
                // connection excluded, so the connection driving the turn never sees
                // it (`T-118`; the id it carries arrives on the stream's own `start`
                // chunk instead). The second means "accepted, not streaming yet" —
                // already the state.
                //
                // The third is `D134`, and it is ignored ON PURPOSE rather than
                // unimplemented: the agent overrides `onChatRecovery` to return
                // `{ continue: false }`, so an interrupted turn is never continued and
                // a "recovering…" indicator here would promise a retry that is not
                // coming. What the frame IS worth is the line above — it re-armed the
                // turn watchdog before this switch ran, which is exactly right, because
                // a server still talking about a turn is a server that has not
                // abandoned it (`D127`).
                break;
        }
    }
    /**
     * The agent refused this request because the question it carries is
     * already answered, or being answered now (`D192`) — a resend after a
     * reconnect, a second device, a "Perguntar de novo" over an answer this
     * screen had given up on. Either way the transcript is where the answer is
     * or will be, so the socket goes and the transcript is read.
     */
    onRepeatedTurn(frame) {
        if (frame.id !== this.inFlightRequestId) {
            return;
        }
        this.closing = true;
        this.socket?.close();
        this.socket = null;
        this.socketOpen = false;
        if (frame.type === TRAINER_TURN_SETTLED_FRAME) {
            this.resyncNow();
            return;
        }
        this.enterWaiting();
    }
    /**
     * The trainer announced what it is doing (`D191`). Only while a turn is in
     * flight — an announcement for a turn this screen is not waiting on, from
     * another device's question, is not this screen's to show.
     */
    onActivity(frame) {
        const parsed = trainerActivityFrame.safeParse(frame);
        if (!parsed.success || this.inFlightRequestId === null) {
            return;
        }
        if (this.activity !== parsed.data.activity) {
            this.activity = parsed.data.activity;
            this.emit();
        }
    }
    /**
     * The server is offering to replay an ACTIVE stream. ACK it once.
     *
     * The draft is cleared first because the replay is FULL rather than
     * offset-based — a documented limitation `T43` confirmed rather than worked
     * around. Wasted bandwidth, never lost text; but appended to what the first
     * socket already showed, it would be the first half of the answer twice.
     */
    onResumeOffer(frame) {
        const { id } = frame;
        if (id === undefined || this.ackedResumeIds.has(id)) {
            return;
        }
        this.ackedResumeIds.add(id);
        this.clearResumeTimer();
        this.awaitingReplay = true;
        this.setStatus("streaming");
        this.socket?.send(JSON.stringify({ id, type: CHAT_FRAME.STREAM_RESUME_ACK }));
        this.armResumeTimer();
    }
    /**
     * ── `T-58`, HANDLED ─────────────────────────────────────────────────────────
     * "Nothing ACTIVE to resume" is not "nothing to resume". An answer that
     * finished while the phone was asleep leaves a completed buffer that the
     * server will replay to anyone who ACKs the ORIGINAL request id — and it never
     * offers that id, because offering is what it does for active streams only.
     *
     * A client that only answers offers shows the partial text it had when the
     * screen locked, with no error and no gap in any log. This is the one line
     * that prevents it.
     */
    onResumeNone(frame) {
        const requestId = this.inFlightRequestId;
        if (frame.reason !== STREAM_RESUME_NONE_IDLE ||
            requestId === null ||
            this.ackedResumeIds.has(requestId)) {
            return;
        }
        this.ackedResumeIds.add(requestId);
        this.awaitingReplay = true;
        this.socket?.send(JSON.stringify({
            id: requestId,
            type: CHAT_FRAME.STREAM_RESUME_ACK,
        }));
        this.armResumeTimer();
    }
    onResponse(frame) {
        if (this.inFlightRequestId === null) {
            // No turn is in flight, so this frame belongs to one that already
            // settled. Returning here rather than only guarding the `setStatus`
            // below is deliberate: with no turn, EVERY branch under it is inert or
            // harmful. `appendDraft` and `applyToolEvents` write into a draft that
            // no longer exists (`updateDraft` returns at its own null check), and an
            // error chunk would raise `chat.generation_failed` over an answer that
            // finished correctly. A duplicate `done: true` used to self-heal by
            // re-running the settle path; it is now ignored instead, which ends in
            // the same state and no longer depends on that path being re-entrant.
            //
            // Skipping `clearResumeTimer` costs nothing. The timer is armed only
            // where `inFlightRequestId` is non-null, and `finishTurn` — the single
            // place that field becomes null — clears it on every route out of a
            // turn, so there is no timer to clear when this branch is taken.
            return;
        }
        // ── ONLY THIS TURN'S FRAMES (`D192`) ─────────────────────────────────────
        // The agent broadcasts every turn's stream to every socket on the
        // conversation, so a second device with a turn of its own in flight used
        // to take the first device's answer as its own. A frame is this turn's
        // when it carries this turn's request id, or one this screen ACKed for a
        // replay. A continuation's id is minted by the SDK and never told to the
        // client (`openContinuation`), so a continuation accepts what comes.
        if (!this.continuationTurn &&
            frame.id !== undefined &&
            frame.id !== this.inFlightRequestId &&
            !this.ackedResumeIds.has(frame.id)) {
            return;
        }
        this.clearResumeTimer();
        this.setStatus("streaming");
        if (isErrorChunk(frame.body)) {
            // Generation itself failed. This is NOT the truncation case: `T-57`
            // produces no error chunk at all, which is exactly what makes it silent.
            this.settleDraft("failed");
            this.finishTurn();
            this.errorKey = "chat.generation_failed";
            this.setStatus("error");
            return;
        }
        // ── THE SERVER'S ID FOR THIS ANSWER, ON THE FIRST CHUNK (`T-118`) ──────
        // Taken before anything is written into the draft, so the message is keyed
        // correctly for its whole life and the next turn replays it under an id
        // the agent already has a row for — an upsert onto its own answer instead
        // of a second row beside it. A resumed stream replays `start` too, so this
        // re-adopts the same id and changes nothing.
        this.adoptMessageId(startMessageId(frame.body));
        const delta = textOfChunk(frame.body);
        if (delta !== "") {
            if (this.awaitingReplay) {
                // The replay has started, so what is on screen is about to be sent
                // again from the beginning. See `awaitingReplay`.
                this.awaitingReplay = false;
                this.resetDraftText();
            }
            this.appendDraft(delta);
        }
        this.applyToolEvents(toolEventsOfChunk(frame.body));
        if (frame.done === true) {
            // ── THE ONE PLACE `T-57` IS ANSWERED ───────────────────────────────────
            // `done: true` is the server saying the stream ended. It says nothing
            // about whether the ANSWER ended: an object that died mid-turn replays
            // what it buffered and marks it done, with no error frame anywhere — the
            // flag is fabricated by the object that adopted the dead stream. The
            // end-of-answer marker is read instead, from the RAW text, because it is
            // content and so cannot be fabricated after the fact (`D77`).
            // A pending widget changes NOTHING about this verdict, and that is the
            // decision rather than an omission (`D77`). An answer that ends in a
            // question is complete-with-question: the marker is still what says the
            // prose finished, and the widget rides on the same message. An answer
            // whose marker never arrived is still discarded, widget and all — the
            // text may be inverted rather than merely short, and a question attached
            // to a severed claim is a question about something nobody validated. The
            // next turn re-asks from stored progress, which is the copy that cannot
            // be stale.
            const verdict = assessAnswer(this.rawDraft);
            // The socket is closed once the stream has ended, either way. The
            // conversation lives in the Durable Object, not in this connection, and
            // an idle socket keeps an object awake for a conversation nobody is
            // having.
            this.closing = true;
            this.socket?.close();
            this.socket = null;
            this.socketOpen = false;
            if (!verdict.complete) {
                // ── A CUT STREAM IS NOT A CUT ANSWER (`D192`) ───────────────────────
                // `D77` still holds — a severed answer is never shown, not even for a
                // moment — so the partial goes. What changed is what comes next: an
                // object that died mid-replay re-delivers the whole validated answer
                // from its checkpoint, and a continuation this socket never heard
                // (`D191`, an empty `done`) is already stored. The transcript is read
                // until the answer is in it; only a turn that truly has no answer
                // ends with the resend button, and with no error over it.
                this.resetDraftText();
                this.enterWaiting();
                return;
            }
            // The `ask` is read from the RAW text — the message's own text was
            // stripped of the block on arrival, which is exactly why judging or
            // parsing the message copy would find nothing (`D112`).
            this.settleDraft("complete", parseAsk(this.rawDraft));
            this.finishTurn();
            this.persistTranscript();
            this.setStatus("idle");
        }
    }
    // ── Waiting on the stored transcript (`D192`) ────────────────────────────
    /**
     * Stop streaming and wait for the answer in the stored transcript.
     *
     * Reached wherever this class used to tell the person their answer was lost:
     * reconnect attempts exhausted, a resume ACK that replayed nothing, the turn
     * watchdog, a cut stream, an agent saying the turn is still running. In
     * every one of those the agent goes on answering — it keeps generating with
     * nobody attached, and re-runs a turn a deploy killed — so the honest state
     * is "still writing", and the transcript is read until it holds the answer.
     */
    enterWaiting() {
        if (this.inFlightRequestId === null) {
            return;
        }
        this.clearResumeTimer();
        this.clearTurnWatchdog();
        this.awaitingReplay = false;
        this.hidePartial();
        if (this.socket !== null) {
            this.closing = true;
            this.socket.close();
            this.socket = null;
            this.socketOpen = false;
        }
        this.setStatus(this.isOnline() ? "waiting" : "offline");
        this.resyncAttempt = 0;
        this.resyncNow();
    }
    /**
     * Take whatever arrived of this answer off the screen (`D77`): the wait ends
     * in the stored WHOLE answer, never in a partial. A continuation's draft is
     * the message it extends, so that message goes back to what it said before.
     */
    hidePartial() {
        if (this.draftPrefix === "") {
            this.resetDraftText();
            return;
        }
        this.rawDraft = "";
        const { draftId } = this;
        const before = stripAskBlock(joinAnswerSegments(this.draftPrefix));
        this.messages = this.messages.map((message) => message.id === draftId ? { ...message, text: before } : message);
        this.emit();
    }
    /** Read the transcript now, cancelling any read already scheduled. */
    resyncNow() {
        this.clearResync();
        this.resync().catch(() => undefined);
    }
    clearResync() {
        if (this.resyncTimer !== null) {
            clearTimeout(this.resyncTimer);
            this.resyncTimer = null;
        }
    }
    /**
     * The next read, on the delay schedule — and only while the app is in front
     * and online. In the background or offline nothing is read; the signal
     * coming back reads at once.
     */
    scheduleResync() {
        this.clearResync();
        if (this.disposed ||
            this.inFlightRequestId === null ||
            !this.isOnline() ||
            !this.isActive()) {
            return;
        }
        const delays = this.options.resyncDelaysMs ?? DEFAULT_RESYNC_DELAYS_MS;
        const wait = delays[Math.min(this.resyncAttempt, delays.length - 1)] ?? 10_000;
        this.resyncAttempt += 1;
        this.resyncTimer = setTimeout(() => {
            this.resyncTimer = null;
            this.resync().catch(() => undefined);
        }, wait);
    }
    /**
     * One read of the stored transcript, and what it means for the turn.
     *
     * The server is the one place that knows: whether the question arrived,
     * whether the agent is still answering it (`ChatTurnInFlight`), whether the
     * answer is stored. This class used to guess all three with timers, and
     * every guess that fired early told the person something false.
     */
    async resync() {
        const load = this.options.loadHistory;
        if (this.disposed || this.inFlightRequestId === null) {
            return;
        }
        if (load === undefined) {
            // Nothing to read the answer from. A turn that never left is sent; one
            // that did ends with the question offered again, and no claim at all
            // about its answer.
            if (this.unsentOutbound()) {
                this.sendAgain();
                return;
            }
            this.settleUnanswered(null);
            return;
        }
        if (this.resyncing) {
            return;
        }
        this.resyncing = true;
        let read = null;
        try {
            read = normaliseHistory(await load());
        }
        catch {
            read = null;
        }
        finally {
            this.resyncing = false;
        }
        if (this.disposed || this.inFlightRequestId === null) {
            return;
        }
        if (read === null) {
            // The read itself failed — the network again. Still waiting.
            if (this.socket === null) {
                this.setStatus(this.isOnline() ? "waiting" : "offline");
            }
            this.scheduleResync();
            return;
        }
        this.applyStanding(this.standingIn(read), read.messages);
    }
    applyStanding(standing, messages) {
        switch (standing) {
            case "answered":
                this.settleFromHistory(messages);
                return;
            case "in_flight":
                if (this.socket === null) {
                    this.setStatus("waiting");
                }
                this.scheduleResync();
                return;
            case "unsent":
                this.sendAgain();
                return;
            default:
                if (!this.autoResent) {
                    // Once, unprompted: the agent stores the question and refuses a
                    // repeat of one it has answered or is answering, so a resend here
                    // can only start the turn that never ran.
                    this.autoResent = true;
                    this.sendAgain();
                    return;
                }
                this.settleUnanswered(messages);
        }
    }
    /** Where the turn in flight stands in a read (`TurnStanding`). */
    standingIn(read) {
        const { inFlight, messages } = read;
        const unknownStillRunning = inFlight === undefined &&
            Date.now() - this.turnStartedAt < UNKNOWN_IN_FLIGHT_BUDGET_MS;
        if (this.continuationTurn) {
            return this.continuationStanding(messages, inFlight, unknownStillRunning);
        }
        const question = this.turnQuestionId;
        const index = question === null
            ? -1
            : messages.findIndex((message) => message.id === question && message.role === "user");
        if (index === -1) {
            return inFlight?.userMessageId === question && question !== null
                ? "in_flight"
                : "unsent";
        }
        const answered = messages
            .slice(index + 1)
            .some((message) => message.role === "assistant" && message.state === "complete");
        if (answered) {
            return "answered";
        }
        if ((inFlight !== null &&
            inFlight !== undefined &&
            !inFlight.continuation &&
            inFlight.userMessageId === question) ||
            unknownStillRunning) {
            return "in_flight";
        }
        return "unanswered";
    }
    continuationStanding(messages, inFlight, unknownStillRunning) {
        const target = this.continuationOf;
        const holder = target === null
            ? undefined
            : messages.find((message) => message.id === target.messageId);
        if (holder !== undefined &&
            target !== null &&
            (holder.wire ?? holder.text) !== target.text) {
            return "answered";
        }
        if (inFlight?.continuation === true || unknownStillRunning) {
            return "in_flight";
        }
        const widget = holder?.asks?.find((ask) => ask.toolCallId === target?.toolCallId);
        if (this.pendingToolResult !== null &&
            widget !== undefined &&
            widget.output === undefined) {
            return "unsent";
        }
        return "answered";
    }
    /** Send the turn's outbound half again: the question, or the widget answer. */
    sendAgain() {
        this.attempts = 0;
        if (this.continuationTurn) {
            this.toolResultSent = false;
        }
        this.setStatus(this.isOnline() ? "connecting" : "offline");
        this.armTurnWatchdog();
        this.connect({ resend: !this.continuationTurn }).catch(() => undefined);
    }
    /** The answer is stored: the transcript becomes the server's, the turn ends. */
    settleFromHistory(messages) {
        this.dropDraft();
        this.reconcile(messages);
        this.finishTurn();
        this.status = "idle";
        this.errorKey = null;
        this.emit();
    }
    /**
     * The question arrived and nothing answers it, twice over: the button to
     * ask again, and NO error line — nothing the person had was lost, the turn
     * simply never ran.
     */
    settleUnanswered(messages) {
        const question = this.turnQuestionId;
        this.dropDraft();
        if (messages !== null) {
            this.reconcile(messages);
        }
        this.messages = this.messages.map((message) => message.id === question && message.role === "user"
            ? { ...message, resendable: true }
            : message);
        this.finishTurn();
        this.status = "idle";
        this.errorKey = null;
        this.emit();
    }
    /** Remove the turn's draft, whatever id it carries now. */
    dropDraft() {
        const { draftId } = this;
        if (draftId === null) {
            return;
        }
        if (this.draftPrefix !== "") {
            // A continuation's draft IS the message it extends (`D191`): put it
            // back as it was rather than deleting a message the server still has.
            this.discardDraft();
            return;
        }
        this.messages = this.messages.filter((message) => message.id !== draftId);
    }
    /**
     * The resume ACK produced nothing.
     *
     * Reachable when the answer is still being generated (`D19`: nothing streams
     * until it is whole), or when its buffer outlived its retention. Neither is
     * a lost answer, so this ends in a read of the stored transcript
     * (`enterWaiting`, `D192`), never in an error.
     */
    armResumeTimer() {
        this.clearResumeTimer();
        this.resumeTimer = setTimeout(() => {
            this.resumeTimer = null;
            if (this.inFlightRequestId === null) {
                return;
            }
            // Nothing replayed. That said "the answer is gone" (`chat.answer_lost`)
            // and was false whenever the agent was still generating — which, under
            // `D19`, is most of a turn. The transcript knows (`D192`).
            this.enterWaiting();
        }, this.options.resumeTimeoutMs ?? DEFAULT_RESUME_TIMEOUT_MS);
    }
    clearResumeTimer() {
        if (this.resumeTimer !== null) {
            clearTimeout(this.resumeTimer);
            this.resumeTimer = null;
        }
    }
    /**
     * The turn produced nothing at all. See `DEFAULT_TURN_SILENCE_MS` for what
     * "nothing" means and why the window is as long as it is.
     *
     * On expiry it no longer ends the turn (`D192`). It ended it as
     * `chat.turn_stalled` — and a phone frozen in a pocket came back to find it
     * overdue, over an answer the agent had finished and stored meanwhile. Now
     * it stops listening and reads the transcript (`enterWaiting`): the answer
     * if it is there, the wait if the agent says it is still running, and the
     * resend button, with no error line, only if nothing is answering at all.
     */
    armTurnWatchdog() {
        this.clearTurnWatchdog();
        this.turnWatchdog = setTimeout(() => {
            this.turnWatchdog = null;
            if (this.inFlightRequestId === null) {
                return;
            }
            // Silence on the socket is not silence on the server (`D192`): a frozen
            // app comes back to find this overdue, over an answer that finished
            // meanwhile. The transcript says which it is; only a turn with nothing
            // answering it ends, and with the resend button rather than an error.
            this.enterWaiting();
        }, this.options.turnSilenceMs ?? DEFAULT_TURN_SILENCE_MS);
    }
    clearTurnWatchdog() {
        if (this.turnWatchdog !== null) {
            clearTimeout(this.turnWatchdog);
            this.turnWatchdog = null;
        }
    }
    // ── Draft bookkeeping ────────────────────────────────────────────────────
    resetDraftText() {
        this.rawDraft = "";
        this.updateDraft(() => "");
    }
    /**
     * Append what arrived, and show it WITHOUT the end-of-answer marker.
     *
     * The raw copy is what `assessAnswer` judges; the message holds the stripped
     * text and nothing else ever sees the other one. Stripping here rather than
     * at the point of display is what makes "the marker never renders" a property
     * of this class instead of a rule every future component has to remember.
     */
    appendDraft(delta) {
        this.rawDraft += delta;
        // The `ask` block is stripped the same way and for the same reason: the
        // message holds what a component may render, and JSON typing itself out
        // under the prose is not that. `parseAsk` reads the RAW copy instead.
        // A continuation shows the message it resumed above it, a paragraph apart
        // (`D191`); `joinAnswerSegments` is `stripEndMarker` when there is none.
        const text = stripAskBlock(joinAnswerSegments(this.draftPrefix + this.rawDraft));
        if (this.draftPrefix === "") {
            this.updateDraft(() => text);
            return;
        }
        const segments = displaySegments(this.draftPrefix + this.rawDraft);
        const { draftId } = this;
        this.messages = this.messages.map((message) => message.id === draftId ? { ...message, segments, text } : message);
        this.emit();
    }
    updateDraft(next) {
        const { draftId } = this;
        if (draftId === null) {
            return;
        }
        this.messages = this.messages.map((message) => message.id === draftId
            ? { ...message, text: next(message.text) }
            : message);
        this.emit();
    }
    /**
     * Fold tool events into the draft's widgets.
     *
     * ── IDEMPOTENT BY `toolCallId`, WHICH IS WHAT MAKES A REPLAY SAFE ─────────
     * The replay is FULL rather than offset-based (`T43`), so a client that
     * reconnects mid-answer is sent every chunk of it again — the tool call
     * included. Text survives that because `awaitingReplay` empties the draft
     * before the first replayed chunk; a widget cannot be handled the same way,
     * because a widget the person has ALREADY ANSWERED would be re-armed by the
     * reset, throwing away the local answer and re-asking a question they have
     * dealt with. So widgets accumulate by id instead: a second
     * `tool-input-available` for a call already held is a no-op, and so is a
     * `tool-output-available` for one already answered.
     *
     * Anything that is not a well-formed `askUser` call is dropped in silence,
     * for `parseAsk`'s reason: a chat with no control still works as a chat, and
     * one unrenderable widget must not cost the answer it came with. `D123`'s
     * bounds are enforced by the contract's schema and not restated here.
     */
    applyToolEvents(events) {
        const { draftId } = this;
        if (draftId === null || events.length === 0) {
            return;
        }
        const draft = this.messages.find((message) => message.id === draftId);
        if (draft === undefined) {
            return;
        }
        const asks = [...(draft.asks ?? [])];
        const offers = [...(draft.offers ?? [])];
        let changed = false;
        for (const event of events) {
            // Both folds see every event, and at most one of them acts on it: an
            // input event is claimed by `toolName`, an output event by whichever list
            // already holds its `toolCallId`. `||` and not `&&`, and the fold call
            // FIRST — a short circuit here would skip the second list's fold for
            // every event the first one accepted.
            const foldedAsk = foldToolEvent(asks, event);
            const foldedOffer = foldScreenOfferEvent(offers, event);
            changed = foldedAsk || foldedOffer || changed;
        }
        if (!changed) {
            return;
        }
        // Each list is attached only when it has something in it: `ChatMessage`
        // says absent and empty are the same fact, and an empty array here would
        // make "this answer offered nothing" indistinguishable from "this answer
        // made an offer that was refused".
        this.messages = this.messages.map((message) => message.id === draftId
            ? {
                ...message,
                ...(asks.length === 0 ? {} : { asks }),
                ...(offers.length === 0 ? {} : { offers }),
            }
            : message);
        this.emit();
    }
    /** Mark one widget answered, wherever in the transcript it lives. */
    attachOutput(toolCallId, output) {
        this.messages = this.messages.map((message) => {
            const { asks } = message;
            if (asks === undefined ||
                !asks.some((widget) => widget.toolCallId === toolCallId)) {
                return message;
            }
            return {
                ...message,
                asks: asks.map((widget) => widget.toolCallId === toolCallId ? { ...widget, output } : widget),
            };
        });
        this.emit();
    }
    /** The same mark, for an offer the person accepted. */
    attachOfferOutput(toolCallId, output) {
        this.messages = this.messages.map((message) => {
            const { offers } = message;
            if (offers === undefined ||
                !offers.some((offer) => offer.toolCallId === toolCallId)) {
                return message;
            }
            return {
                ...message,
                offers: offers.map((offer) => offer.toolCallId === toolCallId ? { ...offer, output } : offer),
            };
        });
        this.emit();
    }
    /**
     * Re-key the draft to the id the SERVER minted for it (`T-118`).
     *
     * `localId()` names the bubble until this arrives, which is a few hundred
     * milliseconds at most and never zero — the draft has to exist before the
     * first chunk can be written into it. Everything that refers to a message by
     * id goes through `draftId`, which moves with it here.
     *
     * A no-op when the ids already agree, which is the resumed-stream case.
     */
    adoptMessageId(serverId) {
        const { draftId } = this;
        if (serverId === null || draftId === null || draftId === serverId) {
            return;
        }
        // Only a CONTINUATION resumes an earlier message (`D191`). A new turn
        // whose replayed `start` names a message already on screen is the same
        // answer arriving twice — the transcript read it and the replay brought it
        // again — and merging there wrote the answer under itself and sent the
        // doubled text back over the stored row (`D192`). That one is dropped.
        const resumed = this.messages.find((message) => message.id === serverId && message.role === "assistant");
        if (resumed !== undefined && !this.continuationTurn) {
            this.settleFromHistory(this.messages.filter((m) => m.id !== draftId));
            this.closing = true;
            this.socket?.close();
            this.socket = null;
            this.socketOpen = false;
            return;
        }
        this.draftId = serverId;
        if (resumed !== undefined) {
            this.mergeIntoResumed(draftId, resumed);
            return;
        }
        this.messages = this.messages.map((message) => message.id === draftId ? { ...message, id: serverId } : message);
        this.emit();
    }
    /**
     * The stream resumes an EARLIER message — a continuation (`D191`, see
     * {@link draftPrefix}). The empty local draft goes, and the resumed message
     * becomes the draft: it streams again, and the new text lands under the old
     * as its own paragraph. Its widgets stay where they are.
     */
    mergeIntoResumed(localDraftId, resumed) {
        this.draftPrefix = resumed.wire ?? `${resumed.text}${ANSWER_END_MARKER}`;
        this.messages = this.messages
            .filter((message) => message.id !== localDraftId)
            .map((message) => message.id === resumed.id ? { ...message, state: "streaming" } : message);
        this.emit();
    }
    settleDraft(state, ask = null) {
        const { draftId } = this;
        if (draftId === null) {
            return;
        }
        // `wire` on the COMPLETE settle only, and taken here because `finishTurn`
        // clears `rawDraft` a moment later. A `truncated` or `failed` message is
        // never sent back at all (`wireMessages`), so carrying its raw text would
        // be storing a string with no reader — and `rawDraft` on those paths is a
        // partial, which is the one thing that must not be replayed as context.
        const wire = state === "complete" ? { wire: this.draftPrefix + this.rawDraft } : {};
        this.messages = this.messages.map((message) => message.id === draftId ? { ...message, ask, state, ...wire } : message);
        this.emit();
    }
    /**
     * Remove the answer, and offer the question again (`D77`).
     *
     * ── WHY THE ANSWER IS DELETED AND NOT MARKED ──────────────────────────────
     * Because it may be inverted rather than incomplete. "Train to failure every
     * set, unless you" is not eighty per cent of an answer, and a warning below
     * it competes with the text above it for attention — a competition the text
     * wins. The question stays, with a refresh button on it, which is where
     * ChatGPT, Claude and Perplexity all put this.
     */
    discardDraft() {
        const { draftId } = this;
        if (draftId === null) {
            return;
        }
        if (this.draftPrefix !== "") {
            // A cut-off CONTINUATION (`D191`): the message it resumed was a whole
            // answer before it began, so it is put back exactly as it was rather
            // than deleted with the cut segment.
            const restored = stripAskBlock(joinAnswerSegments(this.draftPrefix));
            const wire = this.draftPrefix;
            this.messages = this.messages.map((message) => message.id === draftId
                ? { ...message, state: "complete", text: restored, wire }
                : message);
            this.emit();
            return;
        }
        const index = this.messages.findIndex((message) => message.id === draftId);
        if (index === -1) {
            return;
        }
        const question = this.messages[index - 1];
        this.messages = this.messages
            .filter((message) => message.id !== draftId)
            .map((message) => message.id === question?.id && message.role === "user"
            ? { ...message, resendable: true }
            : message);
        this.emit();
    }
    /** The refresh button, with the answer left where it is. See `abandon`. */
    offerResend() {
        const { draftId } = this;
        const index = this.messages.findIndex((message) => message.id === draftId);
        const question = index === -1 ? undefined : this.messages[index - 1];
        if (question === undefined || question.role !== "user") {
            return;
        }
        this.messages = this.messages.map((message) => message.id === question.id ? { ...message, resendable: true } : message);
        this.emit();
    }
    finishTurn() {
        this.clearResumeTimer();
        this.clearTurnWatchdog();
        this.clearResync();
        this.requestSent = false;
        this.resyncAttempt = 0;
        this.turnQuestionId = null;
        this.continuationOf = null;
        this.pendingToolResult = null;
        this.toolResultSent = false;
        this.autoResent = false;
        // The turn is over by every route into here — answered, discarded,
        // abandoned or failed — so the stored id names a buffer nobody will ACK.
        this.options.pendingTurn?.forget().catch(() => undefined);
        this.inFlightRequestId = null;
        this.activity = null;
        this.sendHeld = false;
        this.draftId = null;
        this.rawDraft = "";
        this.draftPrefix = "";
        this.continuationTurn = false;
        this.awaitingReplay = false;
        this.ackedResumeIds.clear();
        this.attempts = 0;
    }
    /**
     * Everything the agent needs to answer the next turn.
     *
     * The whole transcript is sent, not just the new question: the agent persists
     * what it receives and `runTrainerTurn` classifies EVERY user message for the
     * `D21` refusal gate, so a disclosure made three turns ago must still be in
     * the list. Sending only the latest message would let a refusal be escaped by
     * asking the follow-up separately.
     *
     * A `truncated` or `failed` assistant message is left out. It is on screen
     * because the user should see what arrived, but feeding half a sentence back
     * as context invites the model to continue a claim nobody validated.
     *
     * `ask` is IGNORED here — assistant text only (`D114`): the answer to a
     * question lives in the stored rows the agent reads over the RPC, so
     * replaying the question would be replaying a control, not context.
     */
    wireMessages() {
        const wire = [];
        for (const message of this.messages) {
            // ⚠️ Never a message only the device's copy vouches for (`sendHeld`). The
            // agent upserts what it receives, so one sent from a stale copy is
            // written back into a conversation that may have been cleared since.
            if (message.cached === true) {
                continue;
            }
            if (message.role === "user") {
                // The flag goes out on EVERY send, replays of restored messages
                // included: the agent upserts on the message id, so a later turn that
                // omitted it would strip the flag off an opening already persisted
                // with it, and the transcript would go back to claiming the line.
                wire.push(userMessage(message.id, message.text, message.hidden === true ? { hidden: true } : undefined));
                continue;
            }
            if (message.state === "complete") {
                wire.push({
                    id: message.id,
                    // ⚠️ `message.wire`, NEVER `message.text` (`T-118`). The display copy
                    // has had its end marker and `ask` block stripped, and sending that
                    // back OVERWRITES the server's stored answer with a version
                    // `assessAnswer` will judge truncated forever after. See the field's
                    // docblock. The fallback is for a message that predates the field —
                    // it restores the old behaviour for that one message rather than
                    // sending an empty part.
                    parts: [{ text: message.wire ?? message.text, type: "text" }],
                    role: "assistant",
                });
            }
        }
        return wire;
    }
    // ── Status ───────────────────────────────────────────────────────────────
    setStatus(status) {
        if (this.status === status) {
            return;
        }
        this.status = status;
        if (status !== "error") {
            this.errorKey = null;
        }
        this.emit();
    }
    fail(errorKey) {
        this.closing = true;
        this.socket?.close();
        this.socket = null;
        this.errorKey = errorKey;
        this.status = "error";
        this.finishTurn();
        this.emit();
    }
    emit() {
        this.options.onChange(this.snapshot());
    }
}
//# sourceMappingURL=trainer-socket.js.map